import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, FileText, Calendar, CheckCircle, Clock, AlertCircle,
  TrendingUp, ArrowRight, Plus
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface DashboardStats {
  clients: {
    total: number;
    active: number;
  };
  projects: {
    active: number;
    total: number;
  };
  workflow: {
    pending: number;
  };
  feedback: {
    pending: number;
  };
  items: {
    total: number;
    todo: number;
    in_progress: number;
    completed: number;
    blocked: number;
    overdue: number;
  };
  content: {
    this_week: number;
  };
}

interface TeamWorkload {
  email: string;
  type: string;
  items_count: number;
  estimated_hours: number;
  actual_hours: number;
}

interface RecentActivity {
  type: 'project' | 'workflow' | 'content' | 'plan';
  id: number;
  title: string;
  description?: string;
  timestamp: string;
  status?: string;
}

export default function DashboardOverview() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [teamWorkload, setTeamWorkload] = useState<TeamWorkload[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set auth token from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load unified dashboard stats
      const response = await axios.get(`${API_BASE_URL}/dashboard/stats`);

      if (response.data.success) {
        setStats(response.data.stats);
        setTeamWorkload(response.data.team_workload || []);
        setRecentActivity(response.data.recent_activity || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    {
      label: 'Total Clients',
      value: stats.clients.total,
      icon: Users,
      color: 'bg-blue-500',
      link: '/dashboard/clients'
    },
    {
      label: 'Active Projects',
      value: stats.projects.active,
      icon: Calendar,
      color: 'bg-green-500',
      link: '/dashboard/projects'
    },
    {
      label: 'Pending Reviews',
      value: stats.workflow.pending,
      icon: Clock,
      color: 'bg-yellow-500',
      link: '/dashboard/workflow'
    },
    {
      label: 'Overdue Items',
      value: stats.items.overdue,
      icon: AlertCircle,
      color: 'bg-red-500',
      link: '/dashboard/projects'
    },
    {
      label: 'In Progress',
      value: stats.items.in_progress,
      icon: TrendingUp,
      color: 'bg-purple-500',
      link: '/dashboard/projects'
    },
    {
      label: 'Completed',
      value: stats.items.completed,
      icon: CheckCircle,
      color: 'bg-green-600',
      link: '/dashboard/projects'
    }
  ] : [];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'project':
        return <Calendar className="w-5 h-5 text-blue-500" />;
      case 'workflow':
        return <FileText className="w-5 h-5 text-purple-500" />;
      case 'content':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'plan':
        return <Calendar className="w-5 h-5 text-purple-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    return `${days} days ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">Welcome back! Here's what's happening with your content.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/dashboard/plans/new"
              className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Plan
            </Link>
            <Link
              to="/dashboard/content/new"
              className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Content
            </Link>
            <Link
              to="/dashboard/clients/new"
              className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Client
            </Link>
            <button className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition">
              <Plus className="w-5 h-5 mr-2" />
              Generate Links
            </button>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          <Link to="/dashboard/feedback" className="text-blue-600 hover:text-blue-700 text-sm flex items-center">
            View all
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className="p-6">
          {recentActivity.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No recent activity
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.title}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-sm text-gray-500">
                    {formatTimestamp(activity.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}